/* eslint-disable max-lines */
import { isDefined, toError } from "@clipboard-health/util-ts";
import { getAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import {
  environmentConfig,
  isDevelopmentNodeEnvironment,
  isProductionNodeEnvironment,
} from "@src/appV2/environment";
import { getAppInfo } from "@src/appV2/lib";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import type * as z from "zod";

import { type CbhAppInfo } from "../lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../lib/analytics";

export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

interface SchemaValidationErrorContext {
  url: string;
  method: string;
  queryParams?: Record<string, unknown>;
}

/**
 * Error thrown when API response validation fails with parseSchema enabled.
 * Contains the Zod error for programmatic access to validation issues.
 */
export class SchemaValidationError extends Error {
  readonly zodError: z.ZodError;
  readonly context: SchemaValidationErrorContext;

  constructor(zodError: z.ZodError, context: SchemaValidationErrorContext) {
    const issues = zodError.issues
      .map((issue) => `  - ${issue.path.join(".")}: ${issue.message}`)
      .join("\n");

    super(`Schema validation failed for ${context.method} ${context.url}:\n${issues}`);

    this.name = "SchemaValidationError";
    this.zodError = zodError;
    this.context = context;
  }
}

export interface CommonApiParams<RequestSchema, ResponseSchema> {
  url: string;
  requestSchema?: z.ZodType<RequestSchema>;
  responseSchema: z.ZodType<ResponseSchema>;
  queryParams?: Record<string, any>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * When true, returns the parsed response data with Zod transformations applied
   * (e.g., coerced dates). When false (default), returns raw response data.
   */
  parseSchema?: boolean;
}

interface ApiParams<RequestSchema, ResponseSchema> extends CommonApiParams<
  RequestSchema,
  ResponseSchema
> {
  data?: RequestSchema;
}

export const apiClient = axios.create({
  baseURL: environmentConfig.REACT_APP_BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    const authHeaderValue = await getAuthHeaderProvider()?.();

    if (isDefined(authHeaderValue)) {
      // FIXME: Type error when spreading ...config.headers in return object below

      config.headers.Authorization = authHeaderValue;
    }

    return {
      ...config,
      headers: config.headers,
    };
  },
  async (error) => {
    if (error instanceof Error) {
      throw error;
    }
  }
);

// Locally caching app info to avoid calling getAppInfo on every request
let cachedAppInfo: Awaited<CbhAppInfo> | undefined;
async function getOrCreateAppInfo() {
  // FIXME: Automatically disabled during ESLint config update
  cachedAppInfo ||= await getAppInfo();

  return cachedAppInfo;
}

/**
 * This interceptor is used to add the app info to the request headers.
 * This isn't covered by unit tests because these interceptors get called
 * when the module is initialized, meaning we can't reliably mock the modules used within them.
 */
apiClient.interceptors.request.use(async (config) => {
  const appInfo = await getOrCreateAppInfo();

  config.headers["App-Version"] = appInfo.version;
  config.headers["App-Build"] = appInfo.build;
  config.headers["App-OTA-Build-Id"] = appInfo.otaBuildId;

  return config;
});

/**
 * Custom property to track whether a request has already been retried after a
 * 401 or 403 response. Prevents infinite retry loops.
 */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  hasRetried401?: boolean;
  hasRetried403?: boolean;
}

function requestHasAuthorizationHeader(request: InternalAxiosRequestConfig): boolean {
  const headerValue = request.headers?.Authorization ?? request.headers?.authorization;
  return typeof headerValue === "string" && headerValue.length > 0;
}

/**
 * Response interceptor that handles 401 Unauthorized errors by:
 * 1. Force refreshing the auth token
 * 2. Retrying the original request once with the new token
 *
 * This works around an Amplify v6 bug where tokens aren't auto-refreshed
 * for CUSTOM_WITHOUT_SRP auth flow.
 * See: https://github.com/aws-amplify/amplify-js/issues/14618
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    // Only retry on 401 errors, and only once per request
    if (
      error.response?.status === 401 &&
      isDefined(originalRequest) &&
      !originalRequest.hasRetried401
    ) {
      originalRequest.hasRetried401 = true;

      logEvent(APP_V2_APP_EVENTS.AUTH_401_RETRY_STARTED, {
        url: originalRequest.url,
        method: originalRequest.method,
      });

      try {
        // Force refresh the auth token
        const newAuthHeaderValue = await getAuthHeaderProvider()?.(true);

        if (isDefined(newAuthHeaderValue)) {
          originalRequest.headers.Authorization = newAuthHeaderValue;

          // Retry the request with the new token
          const retryResponse = await apiClient.request(originalRequest);

          logEvent(APP_V2_APP_EVENTS.AUTH_401_RETRY_SUCCESS, {
            url: originalRequest.url,
            method: originalRequest.method,
          });

          return retryResponse;
        }
        logEvent(APP_V2_APP_EVENTS.AUTH_401_RETRY_FAILED, {
          url: originalRequest.url,
          method: originalRequest.method,
          reason: "auth_header_unavailable",
        });
      } catch (retryError) {
        logError(APP_V2_APP_EVENTS.AUTH_401_RETRY_FAILED, {
          error: toError(retryError),
          metadata: {
            url: originalRequest.url,
            method: originalRequest.method,
          },
        });
        // Fall through to throw the original error
      }
    }

    // API Gateway's JWT authorizer returns 403 (not 401) when the Authorization
    // header is missing entirely. Without recovery, callers retry forever and
    // the user is stuck on a broken app with no way to self-heal. We attempt
    // one last-chance token refresh; if we still can't produce a token, force
    // a Cognito sign-out so the app redirects to /login.
    //
    // The `!requestHasAuthorizationHeader(...)` discriminator keeps this
    // narrowly targeted at the missing-header case and avoids interfering
    // with legitimate permission-denied 403s from downstream services.
    if (
      error.response?.status === 403 &&
      isDefined(originalRequest) &&
      !originalRequest.hasRetried403 &&
      !requestHasAuthorizationHeader(originalRequest)
    ) {
      originalRequest.hasRetried403 = true;

      const authHeaderProvider = getAuthHeaderProvider();
      // If the provider isn't mounted yet (cold-start race), let the request
      // fail naturally. We shouldn't sign out a user who hasn't had a chance
      // to authenticate yet.
      if (!isDefined(authHeaderProvider)) {
        throw error;
      }

      logEvent(APP_V2_APP_EVENTS.AUTH_403_NO_AUTH_HEADER_DETECTED, {
        url: originalRequest.url,
        method: originalRequest.method,
      });

      // Attempt a last-chance force-refresh of the auth token. We split
      // the refresh from the retry so that a non-auth failure of the retry
      // request (e.g. 500, network error, legitimate permission-denied)
      // doesn't cause us to incorrectly sign the user out.
      let newAuthHeaderValue: Awaited<ReturnType<typeof authHeaderProvider>>;
      try {
        newAuthHeaderValue = await authHeaderProvider(true);
      } catch (refreshError) {
        logError(APP_V2_APP_EVENTS.AUTH_403_RETRY_WITH_NEW_TOKEN_FAILED, {
          error: toError(refreshError),
          metadata: {
            url: originalRequest.url,
            method: originalRequest.method,
          },
        });
        // Fall through to signOut — auth is broken.
      }

      if (isDefined(newAuthHeaderValue)) {
        // Token refresh succeeded. The user's auth is fine; any failure
        // from the retried request is unrelated to auth, so propagate it
        // instead of signing the user out.
        originalRequest.headers.Authorization = newAuthHeaderValue;
        try {
          const retryResponse = await apiClient.request(originalRequest);
          logEvent(APP_V2_APP_EVENTS.AUTH_403_RETRY_WITH_NEW_TOKEN_SUCCESS, {
            url: originalRequest.url,
            method: originalRequest.method,
          });
          return retryResponse;
        } catch (retryError) {
          logError(APP_V2_APP_EVENTS.AUTH_403_RETRY_WITH_NEW_TOKEN_FAILED, {
            error: toError(retryError),
            metadata: {
              url: originalRequest.url,
              method: originalRequest.method,
            },
          });
          throw retryError;
        }
      }

      logEvent(APP_V2_APP_EVENTS.AUTH_403_AUTO_SIGN_OUT, {
        url: originalRequest.url,
        method: originalRequest.method,
      });
      try {
        await getCognitoSingleton().signOut();
      } catch (signOutError) {
        logError(APP_V2_APP_EVENTS.AUTH_403_AUTO_SIGN_OUT_FAILED, {
          error: toError(signOutError),
          metadata: {
            url: originalRequest.url,
            method: originalRequest.method,
          },
        });
      }
    }

    throw error;
  }
);

export async function get<RequestSchema, ResponseSchema>({
  url,
  queryParams,
  requestSchema,
  responseSchema,
  parseSchema = false,
  signal,
}: CommonApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(queryParams);
  }

  const response = await apiClient.get<ResponseSchema>(url, {
    params: queryParams,
    signal,
  });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "GET", parseSchema },
      });
    }

    if (isDevelopmentNodeEnvironment() || parseSchema) {
      throw new SchemaValidationError(result.error, { url, method: "GET", queryParams });
    }

    return response;
  }

  if (parseSchema) {
    return { ...response, data: result.data };
  }

  return response;
}

export async function post<RequestSchema, ResponseSchema>({
  url,
  data,
  queryParams,
  requestSchema,
  responseSchema,
  headers,
  parseSchema = false,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.post<ResponseSchema>(url, data, {
    params: queryParams,
    ...(headers ? { headers } : {}),
  });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "POST", queryParams, parseSchema },
      });
    }

    if (isDevelopmentNodeEnvironment() || parseSchema) {
      throw new SchemaValidationError(result.error, { url, method: "POST", queryParams });
    }

    return response;
  }

  if (parseSchema) {
    return { ...response, data: result.data };
  }

  return response;
}

export async function put<RequestSchema, ResponseSchema>({
  url,
  data,
  queryParams,
  requestSchema,
  responseSchema,
  parseSchema = false,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.put<ResponseSchema>(url, data, { params: queryParams });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "PUT", queryParams, parseSchema },
      });
    }

    if (isDevelopmentNodeEnvironment() || parseSchema) {
      throw new SchemaValidationError(result.error, { url, method: "PUT", queryParams });
    }

    return response;
  }

  if (parseSchema) {
    return { ...response, data: result.data };
  }

  return response;
}

export async function patch<RequestSchema, ResponseSchema>({
  url,
  data,
  queryParams,
  requestSchema,
  responseSchema,
  parseSchema = false,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.patch<ResponseSchema>(url, data, { params: queryParams });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "PATCH", queryParams, parseSchema },
      });
    }

    if (isDevelopmentNodeEnvironment() || parseSchema) {
      throw new SchemaValidationError(result.error, { url, method: "PATCH", queryParams });
    }

    return response;
  }

  if (parseSchema) {
    return { ...response, data: result.data };
  }

  return response;
}

export async function remove<RequestSchema, ResponseSchema>({
  url,
  data,
  requestSchema,
  responseSchema,
  queryParams,
  parseSchema = false,
}: ApiParams<RequestSchema, ResponseSchema>): Promise<ApiResponse<ResponseSchema>> {
  if (requestSchema) {
    requestSchema.parse(data);
  }

  const response = await apiClient.delete<ResponseSchema>(url, { params: queryParams });

  const result = await responseSchema.safeParseAsync(response.data);

  if (!result.success) {
    if (isProductionNodeEnvironment()) {
      logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
        errors: result.error.errors,
        metadata: { url, method: "DELETE", parseSchema },
      });
    }

    if (isDevelopmentNodeEnvironment() || parseSchema) {
      throw new SchemaValidationError(result.error, { url, method: "DELETE", queryParams });
    }

    return response;
  }

  if (parseSchema) {
    return { ...response, data: result.data };
  }

  return response;
}
/* eslint-enable max-lines */
